import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { useActiveGroup } from '../hooks/useActiveGroup';
import { Plus, Calendar, DollarSign, User, FileText, Trash2, Pencil, Check, Loader2, AlertCircle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { usePlanFeatures } from '../hooks/usePlanFeatures';

// Helper function to parse date strings in local timezone
const parseDateLocal = (dateString: string): Date => {
  // For YYYY-MM-DD format, ensure it's interpreted in local timezone
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in JS Date
  }
  // For other formats, use standard Date parsing
  return new Date(dateString);
};

interface Resenha {
  id: string;
  data: string;
  total: number;
  status: string;
  responsavel: {
    id: string;
    nome: string;
  };
  jogadores: {
    count: number;
    paid_count: number;
  };
}

const ResenhaPage: React.FC = () => {
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [resenhas, setResenhas] = useState<Resenha[]>([]);
  const [finalizing, setFinalizing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { supabase } = useSupabase();
  const { activeGroup } = useActiveGroup();
  const { limits } = usePlanFeatures();

 const fetchResenhas = async () => {
  if (!activeGroup) return;

  setLoading(true);
  setError(null);

  try {
    const startDate = `${filterMonth}-01`;

    const year = Number(filterMonth.slice(0, 4));
    const month = Number(filterMonth.slice(5, 7)) - 1;

    const end = new Date(year, month + 1, 0); // último dia do mês
    const endDate = end.toISOString().split('T')[0];

    console.log('Fetching resenhas for period:', { startDate, endDate });

    const { data, error } = await supabase
      .from('resenhas')
      .select(`
        id,
        data,
        total,
        status,
        responsavel:jogadores!resenhas_responsavel_id_fkey(id, nome),
        jogadores:resenhas_jogadores(
          jogador_id,
          pago
        )
      `)
      .eq('grupo_id', activeGroup.id)
      .gte('data', startDate)
      .lte('data', endDate)
      .order('data', { ascending: false });

    if (error) throw error;

    const processedResenhas = data?.map(resenha => ({
      ...resenha,
      jogadores: {
        count: resenha.jogadores.length,
        paid_count: resenha.jogadores.filter((j: any) => j.pago).length
      }
    })) || [];

    setResenhas(processedResenhas);
  } catch (err: any) {
    console.error('Erro ao buscar resenhas:', err);
    setError(err.message || 'Erro ao carregar resenhas');
  } finally {
    setLoading(false);
  }
};




  useEffect(() => {
    fetchResenhas();
  }, [activeGroup, filterMonth, supabase]);

  const handleFinalize = async (resenhaId: string) => {
    if (!confirm('Deseja finalizar esta resenha? Isso marcará todos os pagamentos como concluídos.')) {
      return;
    }
    
    setFinalizing(resenhaId);
    
    try {
      // Update resenha status
      const { error: statusError } = await supabase
        .from('resenhas')
        .update({ status: 'finalizada' })
        .eq('id', resenhaId);
        
      if (statusError) throw statusError;
      
      // Mark all participants as paid
      const { error: participantsError } = await supabase
        .from('resenhas_jogadores')
        .update({ pago: true })
        .eq('resenha_id', resenhaId);
        
      if (participantsError) throw participantsError;
      
      // Update local state
      setResenhas(prev => prev.map(resenha => {
        if (resenha.id === resenhaId) {
          return {
            ...resenha,
            status: 'finalizada',
            jogadores: {
              ...resenha.jogadores,
              paid_count: resenha.jogadores.count
            }
          };
        }
        return resenha;
      }));
      
    } catch (err: any) {
      console.error('Erro ao finalizar resenha:', err);
      alert('Erro ao finalizar resenha: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setFinalizing(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta resenha? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    setDeleting(id);
    
    try {
      const { error } = await supabase
        .from('resenhas')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      // Update local state
      setResenhas(prev => prev.filter(resenha => resenha.id !== id));
      
    } catch (err: any) {
      console.error('Erro ao excluir resenha:', err);
      alert('Erro ao excluir resenha: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setDeleting(null);
    }
  };

  const generatePDF = async (resenha: Resenha) => {
    try {
      // Fetch complete resenha data with participants
      const { data, error } = await supabase
        .from('resenhas')
        .select(`
          *,
          responsavel:jogadores!resenhas_responsavel_id_fkey(nome),
          jogadores:resenhas_jogadores(
            jogador:jogadores(id, nome),
            valor_gasto,
            valor_devido,
            pago,
            descricao
          )
        `)
        .eq('id', resenha.id)
        .single();
        
      if (error) throw error;
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let yPos = 20;

      // Title
      doc.setFontSize(20);
      doc.text('Resenha', pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Date and Responsible
      doc.setFontSize(12);
      doc.text(`Data: ${parseDateLocal(data.data).toLocaleDateString('pt-BR')}`, 20, yPos);
      yPos += 7;
      doc.text(`Responsável: ${data.responsavel.nome}`, 20, yPos);
      yPos += 7;
      doc.text(`Total: ${data.total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, 20, yPos);
      yPos += 15;

      // Summary
      doc.setFontSize(14);
      doc.text('Resumo', 20, yPos);
      yPos += 10;

      doc.setFontSize(12);
      const totalGasto = data.jogadores.reduce((sum: number, j: any) => sum + j.valor_gasto, 0);
      const valorPorPessoa = totalGasto / data.jogadores.length;

      doc.text(`Total gasto: ${totalGasto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, 20, yPos);
      yPos += 7;
      doc.text(`Valor por pessoa: ${valorPorPessoa.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`, 20, yPos);
      yPos += 15;

      // Participants table
      doc.setFontSize(14);
      doc.text('Participantes', 20, yPos);
      yPos += 10;

      doc.setFontSize(12);
      const headers = ['Nome', 'Gastou', 'Deve Pagar', 'Diferença', 'Status'];
      const rows = data.jogadores.map((j: any) => {
        const diferenca = j.valor_gasto - valorPorPessoa;
        return [
          j.jogador.nome,
          j.valor_gasto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          valorPorPessoa.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          diferenca.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
          j.pago ? 'Pago' : 'Pendente'
        ];
      });

      (doc as any).autoTable({
        head: [headers],
        body: rows,
        startY: yPos,
        margin: { left: 20 },
        styles: { fontSize: 10 },
        headStyles: { fillColor: [0, 255, 127] }
      });

      doc.save(`resenha-${data.data}.pdf`);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      alert('Erro ao gerar PDF');
    }
  };

  // Check if user has access to resenha feature
  if (!limits.hasResenha) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold mb-2">Recurso Premium</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
          O recurso de Resenha está disponível apenas para assinantes dos planos Professional e Premium.
        </p>
        <Link to="/pricing" className="btn-primary">
          Ver planos
        </Link>
      </div>
    );
  }

  if (!activeGroup) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Nenhum grupo selecionado</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Selecione um grupo ativo para gerenciar resenhas
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-2">Resenhas</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Gerencie as resenhas do seu grupo
          </p>
          <div className="block sm:hidden">
            <button
              onClick={() => navigate('/resenha/nova')}
              className="btn-primary w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Resenha
            </button>
          </div>
        </div>
        <div className="hidden sm:flex sm:justify-between sm:items-center">
          <div></div>
          <button
            onClick={() => navigate('/resenha/nova')}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Resenha
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex items-center gap-4">
          <div>
            <label htmlFor="filterMonth" className="block text-sm font-medium mb-1">
              Mês
            </label>
            <input
              type="month"
              id="filterMonth"
              className="input py-1.5"
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
            />
          </div>
          <div className="mt-auto">
            <button 
              onClick={fetchResenhas}
              className="btn-outline py-1.5"
              title="Atualizar"
            >
              <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p>Carregando resenhas...</p>
        </div>
      ) : resenhas.length === 0 ? (
        <div className="card p-8 text-center">
          <Calendar className="w-16 h-16 text-primary-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Nenhuma resenha encontrada</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Crie sua primeira resenha para começar a gerenciar os gastos do grupo
          </p>
          <button
            onClick={() => navigate('/resenha/nova')}
            className="btn-primary"
          >
            Criar primeira resenha
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {resenhas.map((resenha) => (
            <div
              key={resenha.id}
              className="card hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/resenha/${resenha.id}`)}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <span className="font-medium">
                      {parseDateLocal(resenha.data).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <span className={`text-sm px-2 py-0.5 rounded-full capitalize ${
                    resenha.status === 'finalizada'
                      ? 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                      : 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400'
                  }`}>
                    {resenha.status}
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Responsável: {resenha.responsavel.nome}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Total: {resenha.total.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      })}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t dark:border-dark-border">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        {resenha.jogadores.count} participantes •{' '}
                        {resenha.jogadores.paid_count} pagos
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {resenha.status === 'pendente' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFinalize(resenha.id);
                          }}
                          className="p-2 text-green-500 hover:text-green-700"
                          title="Finalizar"
                          disabled={finalizing === resenha.id}
                        >
                          {finalizing === resenha.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <Check className="w-5 h-5" />
                          )}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/resenha/${resenha.id}/editar`);
                        }}
                        className="p-2 text-blue-500 hover:text-blue-700"
                        title="Editar"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          generatePDF(resenha);
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        title="Gerar PDF"
                      >
                        <FileText className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(resenha.id);
                        }}
                        className="p-2 text-red-500 hover:text-red-700"
                        title="Excluir"
                        disabled={deleting === resenha.id}
                      >
                        {deleting === resenha.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ResenhaPage; 